import { Fragment, useMemo, useState } from "react";

import { Badge, Banner, Empty, Search } from "../ui";
import { Card, TurnBodies, dash, fmtInt, fmtMs, num, shortId } from "./format";
import type { EvalTurn, SuiteDetail } from "./types";

// ============================================================ failures tab

/**
 * Every failing turn, grouped by the check that failed. Grouped rather than flat
 * because failures are not independent events — one broken rail produces forty
 * rows, and a flat list makes that look like forty problems.
 */
export function FailuresTab({ detail }: { detail: SuiteDetail | null }) {
  const rows = detail?.failures ?? [];
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openTurns, setOpenTurns] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.case_id, r.user_text, r.reply_text, (r.fails ?? []).join(" "), r.turn_owner, r.lang].some((v) =>
            String(v ?? "").toLowerCase().includes(q)
          )
        )
      : rows;
    const map = new Map<string, EvalTurn[]>();
    for (const row of filtered) {
      const labels = (row.fails ?? []).length ? (row.fails as string[]) : ["(no labelled check)"];
      for (const label of labels) {
        const list = map.get(label) ?? [];
        list.push(row);
        map.set(label, list);
      }
    }
    return [...map.entries()]
      .map(([label, items]) => ({ label, items }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [rows, query]);

  // A turn failing several checks appears under each, so the group counts sum to
  // more than the turn count. The header says so rather than letting the reader
  // add them up wrong.
  const distinctTurns = useMemo(() => {
    const seen = new Set<string>();
    for (const g of groups) for (const r of g.items) seen.add(`${r.conversation_id}#${r.turn_index}`);
    return seen.size;
  }, [groups]);

  return (
    <Card
      title="Failures"
      note={
        <>
          <span className="strong">{fmtInt(detail?.failures_total)}</span> failing turns scored ·{" "}
          <span className="strong">{fmtInt(detail?.failures_shown)}</span> shown
          {num(detail?.failures_total) > num(detail?.failures_shown)
            ? ` — list capped at ${fmtInt(detail?.sample_limit)}`
            : ""}
          . A turn failing several checks appears under each, so the group counts sum to more than{" "}
          {fmtInt(distinctTurns)} turns.
        </>
      }
      actions={
        <div style={{ width: 260 }}>
          <Search value={query} onChange={setQuery} placeholder="filter case, text, lane…" ariaLabel="Filter failures" />
        </div>
      }
    >
      {!rows.length ? (
        <Banner tone="good">No failing turns in this run and filter set.</Banner>
      ) : !groups.length ? (
        <Empty title="Nothing matches" body={`No failing turn matches “${query}”.`} />
      ) : (
        <div className="col">
          {groups.map((group) => (
            <div key={group.label} className="card" style={{ padding: 0 }}>
              <button
                type="button"
                className="step-row"
                onClick={() => setOpenGroups((g) => ({ ...g, [group.label]: !g[group.label] }))}
              >
                <span className="muted">{openGroups[group.label] ? "▾" : "▸"}</span>
                <span className="grow truncate" style={{ color: "var(--critical)" }}>
                  {group.label}
                </span>
                <Badge tone="critical">{group.items.length}</Badge>
              </button>
              {openGroups[group.label] && (
                <div className="table-wrap" style={{ padding: "0 var(--sp-3) var(--sp-3)" }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th style={{ width: 24 }} />
                        <th>Case</th>
                        <th>Turn</th>
                        <th>Segment</th>
                        <th>Lane</th>
                        <th>All failed checks</th>
                        <th>Judge</th>
                        <th className="num">Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((row) => {
                        const key = `${group.label}|${row.conversation_id}#${row.turn_index}`;
                        return (
                          <Fragment key={key}>
                            <tr
                              style={{ cursor: "pointer" }}
                              onClick={() => setOpenTurns((t) => ({ ...t, [key]: !t[key] }))}
                            >
                              <td className="muted">{openTurns[key] ? "▾" : "▸"}</td>
                              <td>{dash(row.case_id)}</td>
                              <td className="muted">
                                {shortId(row.conversation_id, 8)}#{row.turn_index}
                              </td>
                              <td>
                                {dash(row.scam_strata)} × {dash(row.user_strata)} · {dash(row.lang)}
                              </td>
                              <td>{dash(row.turn_owner)}</td>
                              <td
                                className="truncate"
                                style={{ maxWidth: 240, color: "var(--critical)" }}
                                title={(row.fails ?? []).join(", ")}
                              >
                                {(row.fails ?? []).join(", ") || "—"}
                              </td>
                              <td>{dash(row.judge_verdict)}</td>
                              <td className="num">{fmtMs(row.latency_ms)}</td>
                            </tr>
                            {openTurns[key] && (
                              <tr>
                                <td colSpan={8}>
                                  <TurnBodies turn={row} />
                                  <div className="chips" style={{ marginTop: "var(--sp-2)" }}>
                                    {(row.required_actions ?? []).map((a) => (
                                      <Badge
                                        key={`r${a}`}
                                        tone={(row.delivered_actions ?? []).includes(a) ? "good" : "critical"}
                                      >
                                        {a}
                                      </Badge>
                                    ))}
                                    {(row.soft ?? []).map((s) => (
                                      <Badge key={`s${s}`} tone="warning">
                                        {s}
                                      </Badge>
                                    ))}
                                  </div>
                                  {row.judge_reason && (
                                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                      judge: {row.judge_reason}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
